import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isBrowser } from '@backmarket/utils/env/isBrowser'

import type {
  LinkBooster,
  LinkBoosterListType,
} from '~~/shared/api/internal-links/types'

const INTERNAL_LINKS_LEGACY_ENDPOINT =
  '/internal-links/v1/link-boosters' as const
const INTERNAL_LINKS_NEW_ENDPOINT =
  '/api/internal-links/v1/link-booster' as const

export function useLinkBooster() {
  const $httpFetch = useHttpFetch()
  const experiments = useExperiments()
  const logger = useLogger()

  function fetchLinkBoosterLists({
    sourceUrl,
    listTypes,
    timeout,
  }: {
    sourceUrl: string
    listTypes: LinkBoosterListType[]
    timeout?: number
  }): Promise<Record<string, LinkBooster[]>> {
    const options = {
      query: {
        source_url: sourceUrl,
      },
      ...insertIf(!isBrowser() && timeout !== undefined, { timeout }),
    }

    const useInternalLinksService =
      experiments['experiment.newInternalLinksServicePlp'] === 'enabled'

    const response = useInternalLinksService
      ? $httpFetch(INTERNAL_LINKS_NEW_ENDPOINT, options).catch(
          (error: unknown) => {
            logger?.error(
              '[linkBoosters] internal-links service failed, falling back to internal-linking',
              { error },
            )

            return $httpFetch(INTERNAL_LINKS_LEGACY_ENDPOINT, options)
          },
        )
      : $httpFetch(INTERNAL_LINKS_LEGACY_ENDPOINT, options)

    return response.then((data) =>
      Object.fromEntries(
        listTypes.map((listType) => [
          listType,
          data.linkLists
            .find((list) => list.listId === listType)
            ?.links.map(({ link, title }) => ({
              title,
              relativeLink: link.relativeHref,
            })) || [],
        ]),
      ),
    )
  }

  function fetchLinkBoosters({
    sourceUrl,
    listType,
    timeout,
  }: {
    sourceUrl: string
    listType: LinkBoosterListType
    timeout?: number
  }): Promise<LinkBooster[]> {
    return fetchLinkBoosterLists({
      sourceUrl,
      listTypes: [listType],
      timeout,
    }).then((lists) => lists[listType] ?? [])
  }

  return { fetchLinkBoosters, fetchLinkBoosterLists }
}
